import React, { useEffect, useMemo, useState } from "react";
import { Box, Chip, CircularProgress, Typography } from "@mui/material";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import NewReleasesOutlinedIcon from "@mui/icons-material/NewReleasesOutlined";
import GavelOutlinedIcon from "@mui/icons-material/GavelOutlined";
import { getFacilityCancelImpact } from "@services/facilityManagement";
import type {
  CancelImpactDeadline,
  CancelImpactUrgency,
  FacilityCancelImpact,
} from "../../types/cancelImpact";

export interface CancelImpactSectionProps {
  facilityId: number;
  accessEndDate: string;
  /** When false, omit archive banner (parent already shows one). Default true. */
  showArchiveBanner?: boolean;
}

type TileKey =
  | "users"
  | "documents"
  | "regulatory"
  | "exclusions";

interface TileDef {
  key: TileKey;
  label: string;
  value: number;
  icon: React.ReactNode;
}

const URGENCY_CHIP: Record<
  CancelImpactUrgency,
  { bg: string; color: string; border: string }
> = {
  urgent: { bg: "#fde7e9", color: "#a4262c", border: "#d13438" },
  soon: { bg: "#fff4ce", color: "#8a6116", border: "#fce100" },
  normal: { bg: "#e8f7fb", color: "#065b70", border: "#0a829d" },
};

function formatOnboarded(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

function formatDueDate(isoDate: string): string {
  const d = new Date(`${isoDate}T00:00:00`);
  if (Number.isNaN(d.getTime())) return isoDate;
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function buildTiles(data: FacilityCancelImpact): TileDef[] {
  const tiles: TileDef[] = [];
  const usersTotal = data.users?.total;
  if (typeof usersTotal === "number" && usersTotal > 0) {
    tiles.push({
      key: "users",
      label: "Users",
      value: usersTotal,
      icon: <PeopleOutlineIcon sx={{ fontSize: 22, color: "#065b70" }} />,
    });
  }
  if (
    typeof data.documents_analyzed_12mo === "number" &&
    data.documents_analyzed_12mo > 0
  ) {
    tiles.push({
      key: "documents",
      label: "Documents analyzed",
      value: data.documents_analyzed_12mo,
      icon: <DescriptionOutlinedIcon sx={{ fontSize: 22, color: "#065b70" }} />,
    });
  }
  if (
    typeof data.regulatory_updates_flagged_12mo === "number" &&
    data.regulatory_updates_flagged_12mo > 0
  ) {
    tiles.push({
      key: "regulatory",
      label: "Regulatory updates",
      value: data.regulatory_updates_flagged_12mo,
      icon: <NewReleasesOutlinedIcon sx={{ fontSize: 22, color: "#065b70" }} />,
    });
  }
  if (
    typeof data.exclusion_checks_12mo === "number" &&
    data.exclusion_checks_12mo > 0
  ) {
    tiles.push({
      key: "exclusions",
      label: "Exclusion checks",
      value: data.exclusion_checks_12mo,
      icon: <GavelOutlinedIcon sx={{ fontSize: 22, color: "#065b70" }} />,
    });
  }
  return tiles;
}

/**
 * "What you'll lose access to" block for cancel subscription drawer (AB#10917).
 */
export const CancelImpactSection: React.FC<CancelImpactSectionProps> = ({
  facilityId,
  accessEndDate,
  showArchiveBanner = true,
}) => {
  const [data, setData] = useState<FacilityCancelImpact | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getFacilityCancelImpact(facilityId, { accessEndDate })
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(
            err instanceof Error ? err.message : "Unable to load impact metrics.",
          );
          setData(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [facilityId, accessEndDate]);

  const tiles = useMemo(() => (data ? buildTiles(data) : []), [data]);
  const deadlines: CancelImpactDeadline[] = data?.upcoming_deadlines ?? [];
  const since = formatOnboarded(data?.onboarded_at);
  const showCard = tiles.length > 0;
  const showDeadlines = deadlines.length > 0;

  if (loading) {
    return (
      <Box
        sx={{ display: "flex", justifyContent: "center", py: 2 }}
        aria-busy="true"
        aria-label="Loading impact metrics"
      >
        <CircularProgress size={28} sx={{ color: "#0a829d" }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography sx={{ fontSize: "13px", color: "#737373" }} role="status">
        Impact metrics unavailable right now.
      </Typography>
    );
  }

  if (!showCard && !showDeadlines) {
    return null;
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {showCard && (
        <Box
          sx={{
            border: "1px solid #b8e8f0",
            borderRadius: "12px",
            p: { xs: 2, sm: 2.5 },
            backgroundColor: "#f8fdfe",
          }}
          data-testid="cancel-impact-card"
        >
          <Typography
            sx={{ fontSize: "16px", fontWeight: 700, color: "#262626", mb: 0.5 }}
          >
            What you&apos;ll lose access to
          </Typography>
          {since && (
            <Typography sx={{ fontSize: "13px", color: "#737373", mb: 2 }}>
              Tracking compliance since {since}
            </Typography>
          )}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: `repeat(${Math.min(tiles.length, 4)}, 1fr)`,
              },
              gap: 1.5,
            }}
          >
            {tiles.map((tile) => (
              <Box
                key={tile.key}
                sx={{
                  backgroundColor: "#ffffff",
                  border: "1px solid #e5f5f8",
                  borderRadius: "10px",
                  p: 1.75,
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.75,
                }}
                data-testid={`cancel-impact-tile-${tile.key}`}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {tile.icon}
                  <Typography sx={{ fontSize: "12px", color: "#737373", fontWeight: 500 }}>
                    {tile.label}
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: "22px", fontWeight: 700, color: "#065b70" }}>
                  {tile.value}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {showDeadlines && (
        <Box
          sx={{
            border: "1px solid #e5f5f8",
            borderRadius: "12px",
            p: { xs: 2, sm: 2.25 },
            backgroundColor: "#ffffff",
          }}
          data-testid="cancel-impact-deadlines"
        >
          <Typography sx={{ fontSize: "14px", fontWeight: 700, color: "#262626", mb: 1.5 }}>
            Upcoming deadlines
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
            {deadlines.map((d) => {
              const chip = URGENCY_CHIP[d.urgency] ?? URGENCY_CHIP.normal;
              return (
                <Box
                  key={`${d.name}-${d.due_date}`}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1.5,
                  }}
                >
                  <Typography sx={{ fontSize: "14px", color: "#3d2c1a", lineHeight: 1.4 }}>
                    {d.name}
                  </Typography>
                  <Chip
                    size="small"
                    label={formatDueDate(d.due_date)}
                    sx={{
                      backgroundColor: chip.bg,
                      color: chip.color,
                      border: `1px solid ${chip.border}`,
                      fontWeight: 600,
                      fontSize: "12px",
                    }}
                  />
                </Box>
              );
            })}
          </Box>
        </Box>
      )}

      {showArchiveBanner && (
        <Box
          sx={{
            border: "1px solid #e8c49a",
            borderRadius: "12px",
            p: { xs: 1.75, sm: 2 },
            backgroundColor: "#fffbf5",
          }}
          data-testid="cancel-impact-archive-banner"
        >
          <Typography sx={{ fontSize: "13px", color: "#3d2c1a", lineHeight: 1.55 }}>
            After your access ends, facility compliance history will be archived and no
            longer available in UniComply.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default CancelImpactSection;
